/**
 * @fileoverview Professional Invoice Preview Component
 * @task B.INV.6 - Invoice Create page with live preview
 *
 * Renders a professional-looking invoice preview for investor demos
 */

import { formatCurrency, formatDate } from '@lib/utils';
import { useMarket } from '@contexts/MarketContext';
import type { InvoiceLineItem, Customer } from '@lib/demo-data';

export interface InvoicePreviewData {
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  customer: Customer | null;
  lineItems: InvoiceLineItem[];
  notes?: string;
  discount: number;
  taxRate: number;
}

interface InvoicePreviewProps {
  data: InvoicePreviewData;
  className?: string;
  isBrazil?: boolean;
}

export function InvoicePreview({ data, className, isBrazil: isBrazilProp }: InvoicePreviewProps): JSX.Element {
  const marketContext = useMarket();
  const isBrazil = isBrazilProp ?? marketContext.isBrazil;

  const subtotal = data.lineItems.reduce((sum, item) => sum + item.amount, 0);
  const discountAmount = subtotal * (data.discount / 100);
  const taxableAmount = subtotal - discountAmount;
  const taxAmount = taxableAmount * (data.taxRate / 100);
  const total = taxableAmount + taxAmount;

  return (
    <div className={className}>
      {/* Paper Effect */}
      <div className="bg-white rounded-lg shadow-xl border overflow-hidden">
        {/* Header with gradient accent */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6">
          <div className="flex items-start justify-between">
            {/* Company Logo/Name */}
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white/20 backdrop-blur">
                <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Finova</h1>
                <p className="text-blue-100 text-sm">{isBrazil ? 'Serviços Financeiros' : 'Financial Services'}</p>
              </div>
            </div>
            {/* Invoice Title */}
            <div className="text-right">
              <h2 className="text-3xl font-bold text-white tracking-wide">{isBrazil ? 'NF-e' : 'INVOICE'}</h2>
              <p className="text-blue-100 font-mono text-lg mt-1">
                {data.invoiceNumber || (isBrazil ? 'NF-0000' : 'INV-0000')}
              </p>
            </div>
          </div>
        </div>

        {/* Invoice Details */}
        <div className="px-8 py-6 bg-slate-50 border-b">
          <div className="grid grid-cols-3 gap-8">
            {/* From Section */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                {isBrazil ? 'Emitente' : 'From'}
              </h3>
              <div className="text-sm text-slate-700">
                <p className="font-semibold text-slate-900">{isBrazil ? 'Sua Empresa Ltda' : 'Your Company Name'}</p>
                <p>{isBrazil ? 'Rua Comercial, 123' : '123 Business Street'}</p>
                <p>{isBrazil ? 'São Paulo, SP 01310-100' : 'San Francisco, CA 94102'}</p>
                <p className="mt-1">contato@suaempresa.com.br</p>
                <p>{isBrazil ? '+55 (11) 1234-5678' : '+1 (555) 123-4567'}</p>
              </div>
            </div>

            {/* To Section */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                {isBrazil ? 'Destinatário' : 'Bill To'}
              </h3>
              <div className="text-sm text-slate-700">
                {data.customer ? (
                  <>
                    <p className="font-semibold text-slate-900">{data.customer.name}</p>
                    {data.customer.address && <p>{data.customer.address}</p>}
                    {data.customer.email && <p className="mt-1">{data.customer.email}</p>}
                    {data.customer.phone && <p>{data.customer.phone}</p>}
                  </>
                ) : (
                  <p className="text-slate-400 italic">{isBrazil ? 'Selecione um cliente' : 'Select a customer'}</p>
                )}
              </div>
            </div>

            {/* Invoice Info */}
            <div className="text-right">
              <div className="inline-block text-left">
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <span className="text-slate-500">{isBrazil ? 'Data de Emissão:' : 'Issue Date:'}</span>
                  <span className="font-medium text-slate-900">
                    {data.issueDate ? formatDate(data.issueDate) : '-'}
                  </span>
                  <span className="text-slate-500">{isBrazil ? 'Vencimento:' : 'Due Date:'}</span>
                  <span className="font-medium text-slate-900">
                    {data.dueDate ? formatDate(data.dueDate) : '-'}
                  </span>
                  <span className="text-slate-500">{isBrazil ? 'Situação:' : 'Status:'}</span>
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                    {isBrazil ? 'Rascunho' : 'Draft'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Line Items Table */}
        <div className="px-8 py-6">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-slate-200">
                <th className="pb-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {isBrazil ? 'Descrição' : 'Description'}
                </th>
                <th className="pb-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider w-20">
                  {isBrazil ? 'Qtd' : 'Qty'}
                </th>
                <th className="pb-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider w-28">
                  {isBrazil ? 'Preço Unit.' : 'Rate'}
                </th>
                <th className="pb-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider w-28">
                  {isBrazil ? 'Valor' : 'Amount'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.lineItems.length > 0 ? (
                data.lineItems.map((item, index) => (
                  <tr key={item.id || index}>
                    <td className="py-4">
                      <p className="font-medium text-slate-900">{item.description || (isBrazil ? 'Descrição do item' : 'Item description')}</p>
                    </td>
                    <td className="py-4 text-center text-slate-600">{item.quantity}</td>
                    <td className="py-4 text-right text-slate-600">{formatCurrency(item.unitPrice)}</td>
                    <td className="py-4 text-right font-medium text-slate-900">{formatCurrency(item.amount)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-slate-400 italic">
                    {isBrazil ? 'Adicione itens para visualizá-los aqui' : 'Add line items to see them here'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Totals Section */}
        <div className="px-8 py-6 bg-slate-50 border-t">
          <div className="flex justify-end">
            <div className="w-72">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">{isBrazil ? 'Subtotal' : 'Subtotal'}</span>
                  <span className="font-medium text-slate-900">{formatCurrency(subtotal)}</span>
                </div>
                {data.discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>{isBrazil ? `Desconto (${data.discount}%)` : `Discount (${data.discount}%)`}</span>
                    <span>-{formatCurrency(discountAmount)}</span>
                  </div>
                )}
                {data.taxRate > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">{isBrazil ? `Impostos (${data.taxRate}%)` : `Tax (${data.taxRate}%)`}</span>
                    <span className="text-slate-900">{formatCurrency(taxAmount)}</span>
                  </div>
                )}
                <div className="border-t border-slate-200 pt-2 mt-2">
                  <div className="flex justify-between text-lg">
                    <span className="font-semibold text-slate-700">{isBrazil ? 'Total a Pagar' : 'Total Due'}</span>
                    <span className="font-bold text-slate-900">{formatCurrency(total)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notes Section */}
        {data.notes && (
          <div className="px-8 py-4 border-t bg-amber-50/50">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              {isBrazil ? 'Observações' : 'Notes'}
            </h4>
            <p className="text-sm text-slate-600">{data.notes}</p>
          </div>
        )}

        {/* Footer */}
        <div className="px-8 py-6 bg-slate-800 text-white">
          <div className="grid grid-cols-2 gap-8">
            {/* Payment Instructions */}
            <div>
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                {isBrazil ? 'Instruções de Pagamento' : 'Payment Instructions'}
              </h4>
              <div className="text-sm text-slate-300 space-y-1">
                {isBrazil ? (
                  <>
                    <p><span className="text-slate-400">Banco:</span> Banco do Brasil</p>
                    <p><span className="text-slate-400">Agência:</span> 1234-5</p>
                    <p><span className="text-slate-400">Conta:</span> 12345-6</p>
                    <p><span className="text-slate-400">PIX:</span> empresa@finova.com.br</p>
                  </>
                ) : (
                  <>
                    <p><span className="text-slate-400">Bank:</span> First National Bank</p>
                    <p><span className="text-slate-400">Account:</span> 1234567890</p>
                    <p><span className="text-slate-400">Routing:</span> 021000021</p>
                  </>
                )}
              </div>
            </div>

            {/* Terms */}
            <div className="text-right">
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                {isBrazil ? 'Termos e Condições' : 'Terms & Conditions'}
              </h4>
              <p className="text-xs text-slate-400 leading-relaxed max-w-xs ml-auto">
                {isBrazil
                  ? 'O pagamento é devido dentro do prazo especificado. Pagamentos atrasados podem estar sujeitos a multa de 2% e juros de 1% ao mês. Obrigado por sua preferência!'
                  : 'Payment is due within the specified terms. Late payments may be subject to a 1.5% monthly finance charge. Thank you for your business!'}
              </p>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-6 pt-4 border-t border-slate-700 text-center">
            <p className="text-xs text-slate-500">
              {isBrazil
                ? 'Gerado pelo Sistema Financeiro Finova | www.finova.com.br'
                : 'Generated by Finova Financial System | www.finova.com'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
